import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { Copy, EyeOff, Trash2 } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'

import type { MarketingContentStatusType } from '@/api/admin-marketing-content-types'
import {
	useDeleteMarketingContent,
	useDuplicateMarketingContent,
	useReplaceMarketingContentBlocks,
	useUpdateMarketingContent
} from '@/api/api-mutations'
import { useGetMarketingContent } from '@/api/react-query'
import type { ActionGroup } from '@/components/base/action-menu'
import { Alert } from '@/components/base/alert'
import { Button } from '@/components/base/button'
import { Container, ContainerHeader } from '@/components/base/container'
import { Heading } from '@/components/base/heading'
import { Input } from '@/components/base/input'
import { usePrompt } from '@/components/base/prompt'
import { StatusCell } from '@/components/base/status-cell'
import { Text } from '@/components/base/text'
import { Textarea } from '@/components/base/textarea'
import { toast } from '@/components/base/toast'
import { SingleColumnPage } from '@/components/page-layout'
import { UnsavedChangesBlocker } from '@/components/route-modal'
import { useI18n } from '@/i18n/i18n-react'

import { BlockListEditor, Field, isWritableBlockType, type BlockData, type EditableBlock } from './-blocks'

export const Route = createFileRoute('/_authenticated/_general/marketing/content/$contentUuid')({
	component: ContentEditorPage,
	staticData: {
		crumb: ({ context: { L } }) => L.Marketing.Content.Title()
	}
})

const DEFAULT_START_TIME = '00:00'
const DEFAULT_END_TIME = '23:59'

type DateParts = { date: string; time: string }

// Split an epoch into local date (YYYY-MM-DD) and time (HH:mm) parts for the paired inputs. Empty
// parts mean "no value" (open-ended schedule).
const epochToDateParts = (ms: number | null | undefined): DateParts => {
	if (!ms) return { date: '', time: '' }
	const d = new Date(ms)
	const offset = d.getTimezoneOffset() * 60000
	const local = new Date(d.getTime() - offset).toISOString()
	return { date: local.slice(0, 10), time: local.slice(11, 16) }
}

// Combine the paired inputs back into an epoch. No date means no value; a missing time falls back to
// the field's default (00:00 for start, 23:59 for end).
const datePartsToEpoch = (parts: DateParts, fallbackTime: string): number | null => {
	if (!parts.date) return null
	return new Date(`${parts.date}T${parts.time || fallbackTime}`).getTime()
}

function statusColor(status: 'DRAFT' | 'SCHEDULED' | 'ACTIVE' | 'ENDED') {
	switch (status) {
		case 'ACTIVE':
			return 'green' as const
		case 'SCHEDULED':
			return 'blue' as const
		case 'DRAFT':
			return 'grey' as const
		case 'ENDED':
			return 'red' as const
	}
}

// A labelled schedule field made of a date input plus a time input. Picking a date auto-fills the
// default time; clearing the date clears the time. The time stays freely editable afterwards.
function DateTimeField({
	label,
	help,
	value,
	defaultTime,
	onChange
}: {
	label: string
	help?: string
	value: DateParts
	defaultTime: string
	onChange: (next: DateParts) => void
}) {
	return (
		<div className='flex flex-col gap-1.5'>
			<Text className='text-xs font-medium text-ui-fg-subtle'>{label}</Text>
			<div className='grid grid-cols-2 gap-2'>
				<Input
					type='date'
					value={value.date}
					onChange={e => {
						const date = e.target.value
						if (!date) {
							onChange({ date: '', time: '' })
							return
						}
						onChange({ date, time: value.time || defaultTime })
					}}
				/>
				<Input
					type='time'
					value={value.time}
					disabled={!value.date}
					onChange={e => onChange({ ...value, time: e.target.value })}
				/>
			</div>
			{help && <Text className='text-xs text-ui-fg-muted'>{help}</Text>}
		</div>
	)
}

function ContentEditorPage() {
	const { contentUuid } = Route.useParams()
	const { L } = useI18n()
	const navigate = useNavigate()
	const prompt = usePrompt()

	const contentQuery = useGetMarketingContent({ contentUuid })
	const updateContent = useUpdateMarketingContent(true, contentUuid)
	const replaceBlocks = useReplaceMarketingContentBlocks(true, contentUuid)
	const deleteContent = useDeleteMarketingContent(true)
	const duplicateContent = useDuplicateMarketingContent()

	const [name, setName] = useState('')
	const [description, setDescription] = useState('')
	const [status, setStatus] = useState<MarketingContentStatusType>('DRAFT')
	const [startsAt, setStartsAt] = useState<DateParts>({ date: '', time: '' })
	const [endsAt, setEndsAt] = useState<DateParts>({ date: '', time: '' })
	const [metaDirty, setMetaDirty] = useState(false)

	const [blocks, setBlocks] = useState<EditableBlock[]>([])
	const [blocksDirty, setBlocksDirty] = useState(false)

	const isDirty = metaDirty || blocksDirty

	// Read the latest dirty flags and loaded content from the reset effect without listing them as
	// deps (which would re-run the reset on every keystroke). During a combined save, persisting
	// blocks invalidates this content query, so its refetch lands while the metadata update is still
	// in flight. Without this guard that refetch would clear metaDirty and overwrite the form with
	// stale server metadata before (or instead of) the metadata save — losing the edits on failure.
	const metaDirtyRef = useRef(metaDirty)
	metaDirtyRef.current = metaDirty
	const blocksDirtyRef = useRef(blocksDirty)
	blocksDirtyRef.current = blocksDirty
	const loadedUuidRef = useRef<string | null>(null)

	useEffect(() => {
		const c = contentQuery.data
		if (!c) return
		// A refetch of the content already loaded (e.g. the block-save invalidation mid-save) must not
		// clobber unsaved edits: keep the form until both saves land and the dirty flags clear, then
		// let the server value refresh it. Switching to a different content still loads immediately.
		if (c.uuid === loadedUuidRef.current && (metaDirtyRef.current || blocksDirtyRef.current)) return
		loadedUuidRef.current = c.uuid
		setName(c.name)
		setDescription(c.description ?? '')
		setStatus(c.status)
		setStartsAt(epochToDateParts(c.startsAt))
		setEndsAt(epochToDateParts(c.endsAt))
		setMetaDirty(false)
		setBlocks(c.blocks.map(b => ({ uuid: b.uuid, kind: b.kind, type: b.type, data: b.data ?? {} })))
		setBlocksDirty(false)
	}, [contentQuery.data])

	const startsAtEpoch = useMemo(() => datePartsToEpoch(startsAt, DEFAULT_START_TIME), [startsAt])
	const endsAtEpoch = useMemo(() => datePartsToEpoch(endsAt, DEFAULT_END_TIME), [endsAt])

	const windowStatus = useMemo(() => {
		if (!contentQuery.data) return 'DRAFT' as const
		const now = Date.now()
		if (status === 'DRAFT') return 'DRAFT' as const
		if (endsAtEpoch && endsAtEpoch <= now) return 'ENDED' as const
		if (startsAtEpoch && startsAtEpoch > now) return 'SCHEDULED' as const
		return 'ACTIVE' as const
	}, [contentQuery.data, status, startsAtEpoch, endsAtEpoch])

	// Published content whose window has already closed. Computed from the live form values so the
	// warning clears as soon as the manager reschedules.
	const isExpired = status === 'PUBLISHED' && endsAtEpoch !== null && endsAtEpoch <= Date.now()

	const handleSave = async (nextStatus?: MarketingContentStatusType) => {
		const effectiveStatus = nextStatus ?? status

		if (startsAtEpoch && endsAtEpoch && endsAtEpoch <= startsAtEpoch) {
			toast.error(L.Marketing.Campaign.Validation.EndsAtAfterStartsAt())
			return
		}

		// Guard against publishing / saving content that stays hidden because its window is in the past.
		const willBeExpired = effectiveStatus === 'PUBLISHED' && endsAtEpoch !== null && endsAtEpoch <= Date.now()
		if (willBeExpired) {
			const confirmed = await prompt({
				title: L.Marketing.Content.ExpiredTitle(),
				description: L.Marketing.Content.ExpiredSaveConfirm(),
				variant: 'confirmation',
				confirmText: L.Marketing.Content.SaveAnyway(),
				cancelText: L.General.Cancel()
			})
			if (!confirmed) return
		}

		const statusChanged = nextStatus !== undefined && nextStatus !== status

		try {
			if (blocksDirty) {
				const payload: { type: 'BANNER' | 'LIST' | 'MODAL' | 'SEPARATOR'; data: BlockData }[] = []
				for (const b of blocks) {
					if (!isWritableBlockType(b.type)) continue
					payload.push({ type: b.type, data: b.data ?? {} })
				}
				const result = await replaceBlocks.mutateAsync({
					path: { contentUuid },
					body: { blocks: payload }
				})
				if (!result.isSuccess) {
					toast.error(typeof result.response === 'string' ? result.response : L.General.Unknown())
					return
				}
				setBlocksDirty(false)
			}

			if (metaDirty || statusChanged) {
				const result = await updateContent.mutateAsync({
					path: { contentUuid },
					body: {
						name,
						description: description || null,
						status: effectiveStatus,
						startsAt: startsAtEpoch,
						endsAt: endsAtEpoch
					}
				})
				if (!result.isSuccess) {
					toast.error(typeof result.response === 'string' ? result.response : L.General.Unknown())
					return
				}
				setStatus(effectiveStatus)
				setMetaDirty(false)
			}

			toast.success(statusChanged ? L.Marketing.Content.PublishSuccess() : L.Marketing.Content.SaveSuccess())
		} catch (e: any) {
			toast.error(e?.message ?? L.General.Unknown())
		}
	}

	const handleUnpublish = async () => {
		try {
			const result = await updateContent.mutateAsync({
				path: { contentUuid },
				body: { status: 'DRAFT' }
			})
			if (!result.isSuccess) {
				toast.error(typeof result.response === 'string' ? result.response : L.General.Unknown())
				return
			}
			setStatus('DRAFT')
			toast.success(L.Marketing.Content.UnpublishSuccess())
		} catch (e: any) {
			toast.error(e?.message ?? L.General.Unknown())
		}
	}

	const handleDuplicate = async () => {
		try {
			const result = await duplicateContent({ path: { contentUuid } })
			if (result.isSuccess) {
				toast.success(L.Marketing.Content.DuplicateSuccess())
				navigate({ to: '/marketing/content/$contentUuid', params: { contentUuid: result.response.uuid } })
			}
		} catch (e: any) {
			toast.error(e?.message ?? L.General.Unknown())
		}
	}

	const handleDelete = async () => {
		if (!window.confirm(L.Marketing.Content.DeleteConfirm())) return
		try {
			const result = await deleteContent.mutateAsync({ path: { contentUuid } })
			if (!result.isSuccess) {
				toast.error(typeof result.response === 'string' ? result.response : L.General.Unknown())
				return
			}
			navigate({ to: '/marketing/content' })
		} catch (e: any) {
			toast.error(e?.message ?? L.General.Unknown())
		}
	}

	if (contentQuery.isLoading) {
		return (
			<SingleColumnPage>
				<Container className='flex flex-1 items-center justify-center'>
					<Text className='text-ui-fg-subtle'>{L.General.Loading()}</Text>
				</Container>
			</SingleColumnPage>
		)
	}
	if (!contentQuery.data) return null

	const isSaving = updateContent.isPending || replaceBlocks.isPending

	const headerActions: ActionGroup[] = [
		{
			actions: [
				{
					icon: <Copy className='size-4' />,
					label: L.Marketing.Campaign.Action.Duplicate(),
					onClick: handleDuplicate
				},
				...(status === 'PUBLISHED'
					? [
							{
								icon: <EyeOff className='size-4' />,
								label: L.Marketing.Campaign.Action.Unpublish(),
								onClick: handleUnpublish
							}
						]
					: []),
				{
					icon: <Trash2 className='size-4' />,
					label: L.Marketing.Campaign.Action.Delete(),
					onClick: handleDelete
				}
			]
		}
	]

	return (
		<SingleColumnPage>
			<UnsavedChangesBlocker dirty={isDirty} />
			<Container className='flex flex-1 flex-col overflow-hidden'>
				<ContainerHeader
					heading={name || L.Marketing.Content.Title()}
					rightSide={
						<StatusCell color={statusColor(windowStatus)}>
							{L.Marketing.Campaign.WindowStatusEnum(windowStatus)}
						</StatusCell>
					}
					actions={headerActions}
				/>
				<div className='flex-1 overflow-y-auto p-4 sm:p-6'>
					<fieldset disabled={isSaving} className='flex min-w-0 max-w-3xl flex-col gap-6'>
						{status === 'DRAFT' && <Alert variant='info'>{L.Marketing.Content.DraftNotice()}</Alert>}
						{isExpired && endsAtEpoch !== null && (
							<Alert variant='warning'>
								<div className='flex flex-col gap-2'>
									<Text className='font-medium'>{L.Marketing.Content.ExpiredTitle()}</Text>
									<Text className='text-ui-fg-subtle'>
										{L.Marketing.Content.ExpiredNotice(endsAtEpoch)}
									</Text>
									<div className='flex gap-2'>
										<Button
											size='small'
											variant='secondary'
											onClick={() => {
												setEndsAt({ date: '', time: '' })
												setMetaDirty(true)
											}}
										>
											{L.Marketing.Content.ClearEndDate()}
										</Button>
										<Button
											size='small'
											variant='secondary'
											onClick={() => {
												const today = epochToDateParts(Date.now()).date
												setStartsAt({ date: today, time: DEFAULT_START_TIME })
												setEndsAt({ date: today, time: DEFAULT_END_TIME })
												setMetaDirty(true)
											}}
										>
											{L.Marketing.Content.RunToday()}
										</Button>
									</div>
								</div>
							</Alert>
						)}

						<div className='flex flex-col gap-4'>
							<Field label={L.Marketing.Campaign.Field.Name()}>
								<Input
									value={name}
									placeholder={L.Marketing.Campaign.Field.NamePlaceholder()}
									onChange={e => {
										setName(e.target.value)
										setMetaDirty(true)
									}}
								/>
							</Field>
							<div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
								<DateTimeField
									label={L.Marketing.Campaign.Field.StartsAt()}
									help={L.Marketing.Campaign.Help.StartsAt()}
									value={startsAt}
									defaultTime={DEFAULT_START_TIME}
									onChange={next => {
										setStartsAt(next)
										setMetaDirty(true)
									}}
								/>
								<DateTimeField
									label={L.Marketing.Campaign.Field.EndsAt()}
									help={L.Marketing.Campaign.Help.EndsAt()}
									value={endsAt}
									defaultTime={DEFAULT_END_TIME}
									onChange={next => {
										setEndsAt(next)
										setMetaDirty(true)
									}}
								/>
							</div>
							<Field label={L.Marketing.Campaign.Field.Description()}>
								<Textarea
									value={description}
									placeholder={L.Marketing.Campaign.Field.DescriptionPlaceholder()}
									onChange={e => {
										setDescription(e.target.value)
										setMetaDirty(true)
									}}
								/>
							</Field>
						</div>

						<div className='flex flex-col gap-1'>
							<Heading level='h2'>{L.Marketing.Content.Blocks()}</Heading>
							<Text className='max-w-md text-xs text-ui-fg-muted'>
								{L.Marketing.Content.BlocksHelp()}
							</Text>
						</div>

						<BlockListEditor
							blocks={blocks}
							onChange={next => {
								setBlocks(next)
								setBlocksDirty(true)
							}}
						/>
					</fieldset>
				</div>

				<div className='flex items-center justify-end gap-3 border-t border-ui-border-base px-4 py-3 sm:px-6'>
					{isDirty && (
						<Text className='mr-auto text-xs text-ui-fg-muted'>{L.Marketing.Content.UnsavedChanges()}</Text>
					)}
					{status === 'DRAFT' ? (
						<>
							<Button variant='secondary' onClick={() => handleSave()} disabled={!isDirty || isSaving}>
								{L.Marketing.Content.SaveDraft()}
							</Button>
							<Button onClick={() => handleSave('PUBLISHED')} disabled={isSaving}>
								{L.Marketing.Content.SaveAndPublish()}
							</Button>
						</>
					) : (
						<Button onClick={() => handleSave()} disabled={!isDirty || isSaving}>
							{L.Marketing.Campaign.Action.Save()}
						</Button>
					)}
				</div>
			</Container>
		</SingleColumnPage>
	)
}
